import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, UserRoundIcon, ShieldCheckIcon, NetworkIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { ShiftVisual } from './horizon/ShiftVisuals';
import { horizonIntro, horizonShifts, horizonConclusion, type ShiftIconId } from '../data/horizonShifts';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.5,
    ease: EASE
  }
};
const SHIFT_ICONS: Record<ShiftIconId, typeof SparklesIcon> = {
  sparkles: SparklesIcon,
  identity: UserRoundIcon,
  shield: ShieldCheckIcon
};
export function Horizon2028() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: {
    en: string;
    fr: string;
  }) => isFR ? value.fr : value.en;
  return <section className="bg-white px-4 sm:px-6 pb-8" data-id="element-3779" data-mp="3i509x1i78vyf">
      <div className="max-w-[1400px] mx-auto" data-id="element-3780" data-mp="3i5tblo2awv9p">
        {/* Heading */}
        <motion.div {...reveal} className="text-center max-w-4xl mx-auto" data-id="element-3781" data-mp="2hg79aqb8y8kc">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.6rem] leading-[1.06] tracking-tight" data-id="element-3782" data-mp="2chvz2eh1hw1l">
            <span className="block text-[#121212]" data-id="element-3783" data-mp="2r6ioj90ds46j">{pick(horizonIntro.headlineLead)}</span>
            <span className="block text-[#4F46E5]" data-id="element-3784" data-mp="2mdzakaxs7717">{pick(horizonIntro.headlineAccent)}</span>
          </h2>
          <p className="font-sans text-base md:text-[19px] text-[#5B6478] leading-relaxed mt-6" data-id="element-3785" data-mp="305436t2qez21">
            {pick(horizonIntro.intro)}
          </p>
        </motion.div>

        {/* Three shifts */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-12" data-id="element-3786" data-mp="3a3a7fhc0gha2">
          {horizonShifts.map((shift, index) => {
          const Icon = SHIFT_ICONS[shift.icon];
          return <motion.article key={shift.num} {...reveal} transition={{
            duration: 0.5,
            delay: index * 0.06,
            ease: EASE
          }} className={`flex flex-col rounded-2xl border border-[#E8E9EE] bg-white shadow-[0_2px_10px_rgba(18,18,18,0.03)] p-6 lg:p-7 ${index === 2 ? 'md:col-span-2 xl:col-span-1' : ''}`} data-id="element-3787" data-mp="1fr3rk5869cbf" data-mpi={index}>
                {/* Header */}
                <div className="flex items-center gap-4" data-id="element-3788" data-mp="2ef4eqxknq5kh">
                  <div className="shrink-0" data-id="element-3789" data-mp="2egp84jv8jigr">
                    <div className="font-sans text-[17px] text-[#4F46E5]" data-id="element-3790" data-mp="2jf0icvpfzuzi">{shift.num}</div>
                    <span className="block w-6 h-px bg-[#4F46E5] mt-1" aria-hidden="true" data-id="element-3791" data-mp="2nfu5hmv295rc" />
                  </div>
                  <h3 className="flex-1 font-sans text-[12px] font-semibold tracking-[0.16em] uppercase text-[#121212] leading-relaxed" data-id="element-3792" data-mp="2s6amfgy3b9ig">
                    {pick(shift.label)}
                  </h3>
                  <span className="w-12 h-12 shrink-0 rounded-full bg-[#F4F3FE] flex items-center justify-center" data-id="element-3793" data-mp="325i5mkri9ydh">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-3794" data-mp="3754kszqw9oox" />
                  </span>
                </div>

                {/* Stat + product visual */}
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 items-center mt-7" data-id="element-3795" data-mp="qhdad02dq91u">
                  <div className="min-w-0" data-id="element-3796" data-mp="ljbmm1u0dlk1">
                    <div className="font-serif text-[3.4rem] text-[#4F46E5] leading-none" data-id="element-3797" data-mp="gjgu5tul4nwv">{pick(shift.stat)}</div>
                    <p className="font-sans text-[14px] text-[#5B6478] leading-relaxed mt-4" data-id="element-3798" data-mp="1e7qw68udfbnz">{pick(shift.claim)}</p>
                    <a href={shift.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-block font-sans text-[12.5px] text-[#5B6478] hover:text-[#4F46E5] transition-colors duration-200 mt-4" data-id="element-3799" data-mp="197urs8hrhi7w">
                      {pick(shift.source)}
                    </a>
                  </div>
                  <ShiftVisual icon={shift.icon} isFR={isFR} data-id="element-3800" data-mp="1s5xkoydfjzaz" />
                </div>

                {/* Takeaway + pills */}
                <div className="mt-auto pt-7" data-id="element-3801" data-mp="1cnnydfqw545h">
                  <div className="border-t border-[#E8E9EE] pt-5" data-id="element-3802" data-mp="1holdtkkink3a">
                    <p className="font-serif text-[1.35rem] text-[#121212] leading-snug" data-id="element-3803" data-mp="1hlpfvw8o4veu">{pick(shift.takeaway)}</p>
                    <ul className="flex flex-wrap gap-2 mt-4" data-id="element-3804" data-mp="17pw2ybr1ezwj">
                      {shift.pills.map(pill => <li key={pill.en} className="rounded-full bg-[#F1F0FD] px-3.5 py-1.5 font-sans text-[13px] text-[#4F46E5]" data-id="element-3805" data-mp="1vex9ftgb80z6">
                          {pick(pill)}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.article>;
        })}
        </div>

        {/* Conclusion band */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: EASE
      }} className="relative overflow-hidden mt-5 rounded-2xl border border-[#E6E4FA] bg-[#F7F6FE] px-7 py-8 lg:px-10 lg:py-8 flex flex-col lg:flex-row lg:items-center gap-7 lg:gap-12" data-id="element-3806" data-mp="2echwcuzikph5">
          {/* decorative curves */}
          <svg className="absolute right-0 bottom-0 w-[340px] h-full pointer-events-none" viewBox="0 0 340 140" preserveAspectRatio="none" fill="none" aria-hidden="true" data-id="element-3807" data-mp="hg57gws7f3v3">
            <path d="M40 0 C 140 60, 240 90, 340 150" stroke="#4F46E5" strokeOpacity="0.1" data-id="element-3808" data-mp="3kyoot49cgpb4" />
            <path d="M120 0 C 200 50, 270 90, 340 110" stroke="#4F46E5" strokeOpacity="0.08" data-id="element-3809" data-mp="3fzc1kiek9g80" />
            <path d="M0 150 C 120 90, 240 40, 340 20" stroke="#4F46E5" strokeOpacity="0.07" data-id="element-3810" data-mp="3tylk3oq8omf5" />
          </svg>

          <div className="relative flex items-center gap-6 lg:basis-[57%]" data-id="element-3811" data-mp="3pxrv0s1291ko">
            <span className="w-14 h-14 shrink-0 rounded-full bg-white flex items-center justify-center" data-id="element-3812" data-mp="195gom4g7rjix">
              <NetworkIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-3813" data-mp="154wqaxjwm9v7" />
            </span>
            <p className="font-serif text-2xl lg:text-[2rem] leading-[1.15]" data-id="element-3814" data-mp="1wn02uttfrcp">
              <span className="block text-[#121212]" data-id="element-3815" data-mp="6s2cdic6df70">{pick(horizonConclusion.lead)}</span>
              <span className="block text-[#4F46E5]" data-id="element-3816" data-mp="bu1dsmocu21b">{pick(horizonConclusion.accent)}</span>
            </p>
          </div>

          <span className="hidden lg:block relative w-px self-stretch bg-[#4F46E5]/15" aria-hidden="true" data-id="element-3817" data-mp="fs7jdrdgibkn" />

          <div className="relative" data-id="element-3818" data-mp="3f7m0k2xzo7f7">
            <p className="font-serif text-2xl lg:text-[1.75rem] text-[#121212] leading-snug flex flex-wrap items-center gap-y-1" data-id="element-3819" data-mp="3k7qk61u71i9n">
              {/* Same brand lockup as the site header: two separate assets + a CSS divider */}
              <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-11 w-auto object-contain" data-id="element-3820" data-mp="3t1n1928i44i7" />
              <span className="mx-2 text-[#121212]/25 font-sans text-2xl font-light select-none" aria-hidden="true" data-id="element-3821" data-mp="30ig4gnug6xbj">
                |
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[2.35rem] w-auto object-contain" data-id="element-3822" data-mp="2o1ovvvxq7xro" />
              <span className="ml-3" data-id="element-3823" data-mp="2f0qor4rzra4v">{pick(horizonConclusion.brandSuffix)}</span>
            </p>
            <p className="font-sans text-[15px] text-[#5B6478] mt-2" data-id="element-3824" data-mp="2a3qiotv567dr">{pick(horizonConclusion.brandCaption)}</p>
          </div>
        </motion.div>
      </div>
    </section>;
}